import React from 'react';
import { Box, Typography } from '@mui/material';
import { Button } from '@repo/ui';
import { BadgeCheck } from 'lucide-react';

import { font } from 'settings/tokens';
import { stateCardSurfaceSx } from './stateCardSurface';

/**
 * VERA-304 — "Confirm you have this item", as a STATE CARD.
 *
 * ## Why this is a card and not the action tile it replaces
 *
 * It shipped first as a fourth tile in `ActionTileRow`, beside Gift / Sell /
 * Report, on the argument that "a state card that appears once and never returns
 * fits worse than a tile that quietly disappears." That argument is dead on
 * arrival, because the **Gift received / Claim ownership card sits on this same
 * page**, appears once, is resolved, and never returns. Appearing once is the
 * established shape here, not an objection to it.
 *
 * The real distinction is what kind of thing each row holds:
 *
 * | | what it is | the question it answers |
 * |---|---|---|
 * | Gift · Sell · Report | **capabilities** — standing, repeatable, chosen by the user whenever they like | *what can I do with this?* |
 * | Verify possession | a **to-do** — outstanding, terminal, self-clearing | *what needs my attention?* |
 *
 * A menu of capabilities is the wrong container for a to-do, and the symptom was
 * concrete: someone reading "Not verified" on the badge had no reason to go
 * hunting through an action row for the remedy. A state card is the surface this
 * page already uses for "something about this certificate wants your attention",
 * so the prompt now lives where the reader is already looking for prompts.
 *
 * ## What it does NOT replace
 *
 * The `AuthenticityDrawer`'s own "Verify possession" block stays. Two routes to
 * one action is correct here because they answer different approaches: this card
 * is the PROMPT (you did not ask, we are telling you), and the drawer is
 * EXPLAIN-THEN-ACT (you tapped the badge because you wanted to understand it).
 * Both gate on the same `canVerifyPossession`, so neither can advertise something
 * the other has withdrawn.
 *
 * ## Self-clearing, exactly like the gift card
 *
 * The mutation raises the transfer's `completion_method` to `scan_claimed`, the
 * refetched certificate fails `canVerifyPossession`, `active` goes false, and
 * `StateCardStack`'s `Collapse` animates the card away in place. Nothing here
 * needs to know that; it simply stops being rendered as active.
 *
 * ## ⚠️ NOT wrapped in a `<Stack spacing>`, and that is not a style preference
 *
 * MUI `Stack` spaces its children with `& > :not(style) + :not(style) { margin-top }`,
 * a descendant selector that OUTRANKS the single emotion class an `sx` prop writes on
 * a child. So a child asking for a different top margin is silently overruled — and
 * this was measured, not theorised: written as a Stack, the button's own `mt` computed
 * to 12px (the Stack's value) rather than the 20 it asked for. Same shape as the
 * `#vera-shadow-root` specificity trap `stateCardSurface` documents: an authored value,
 * computed, then discarded.
 *
 * `TransferSummarySection` — the card this one is modelled on — already avoids it by
 * laying its three parts out with explicit margins and no Stack. This does the same,
 * so the two cards' internal rhythm is set the same way and can be compared.
 */
/**
 * The one sentence that explains verifying possession, read by BOTH the state card
 * and `AuthenticityDrawer`'s CTA. See the note at the body `Typography` below for why
 * it is one constant rather than two identical strings.
 */
export const VERIFY_POSSESSION_COPY =
  "Possession of this item isn't verified yet. Scan the Vera tag to confirm it, and " +
  'its provenance will read as verified for anyone who looks.'

export const VerifyPossessionCard = ({ onVerify }: { onVerify: () => void }) => (
  <Box sx={stateCardSurfaceSx}>
    {/* The heading treatment is the state-card family's, one size for all four
        cards — see `stateCardSurface` and the sibling cards. Do not raise it
        here alone; the four are meant to read as one family.

        12px to the body, matching the gift card's heading→info gap exactly. */}
    <Typography sx={{ fontSize: font.body, fontWeight: 500, mb: 1.5 }}>
      Confirm you have this item
    </Typography>
    {/* lineHeight 1.5 so descriptive copy doesn't read dense (§15), and separate
        sentences rather than em-dash-joined clauses.

        The copy says what verifying DOES, not merely that it is possible: the
        reader's question is "why would I scan this again", and "its provenance will
        read as verified" is the answer.

        EXPORTED because `AuthenticityDrawer` says the same thing at its own CTA. Two
        copies of one sentence is how the two surfaces drift apart, and the drawer is
        the one nobody re-reads (§36, and §44's argument for a single definition).

        LEGACY NOTE — the earlier wording led with "the item itself has been checked",
        which named the mechanism rather than the thing being established. The user's
        correction was to say POSSESSION, because that is what the scan proves and
        what the provenance row will read.

        It deliberately does NOT promise the Authentic badge will change. That badge
        reports how this page view was REACHED, a different fact (see
        `AuthenticityDrawer`'s note on `canVerifyPossession`). */}
    <Typography sx={{ fontSize: font.label, lineHeight: 1.5 }} color='text.secondary'>
      {VERIFY_POSSESSION_COPY}
    </Typography>
    {/* 20px above the action, against the 12px between heading and body — the same
        proportions the gift card uses, and for the reason stated there: everything
        above DESCRIBES the state and the button ACTS on it, so that gap is the
        card's one real boundary. `apps/app` runs MUI's 8px base, so 2.5 is 20px
        (§29 — a factor copied from a platform file would render 40).

        On the wrapper rather than the Button, matching the gift card: it keeps the
        gap owned by the card's layout instead of by the control, so a future change
        of button component cannot take the spacing with it.

        FULL WIDTH, the shape every card in this stack shares. Primary, because it
        is the only thing to do here and the card exists solely to offer it — unlike
        `Cancel gift`, a reversal, which a solid fill would oversell. */}
    <Box sx={{ mt: 2.5 }}>
      <Button
        className='w-full'
        variant='primary'
        sx={{ paddingX: 2 }}
        startIcon={<BadgeCheck size={16} />}
        onClick={onVerify}
      >
        Verify possession
      </Button>
    </Box>
  </Box>
);

export default VerifyPossessionCard;
